import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CAlert, CButton, CCard, CCardBody, CCardHeader, CSpinner, CStepper } from "@coreui/react-pro";
import { canResumeAddOn, useAddOns, useResumeAddOn } from "../../api/hooks/useAddOns";
import type { ApiError } from "../../api/client";
import { useFailure } from "../../context/useFailure";
import { useToast } from "../../context/useToast";
import { addOnDetailsPath, routePaths } from "../../routes/route-paths";
import { formatDateShort } from "../../lib/dates";

// CStepper's exposed imperative API. Mirrors the End wizard so the two flows stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

export function AddOnResumeWizard() {
  const navigate = useNavigate();
  const { addOnId = "" } = useParams<{ addOnId: string }>();

  const addOns = useAddOns();
  const resumeRemote = useResumeAddOn();
  const { showSuccess } = useToast();
  const { showFailure, clearFailure } = useFailure();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Only the customer's own cancel that has not yet taken effect can be resumed — the same rule the server enforces.
  const addOn = addOns.data?.find(
    (candidate) => candidate.id === addOnId && candidate.held !== null && canResumeAddOn(candidate.held),
  );

  if (addOns.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (addOns.error) {
    return (
      <div>
        <CAlert color="danger">Unable to load your add-on. Please try again.</CAlert>
      </div>
    );
  }

  if (!addOn?.held) {
    return (
      <div>
        <CAlert color="warning">
          There's no cancelled add-on to resume here. <Link to={routePaths.addOns}>Back to add-ons</Link>.
        </CAlert>
      </div>
    );
  }

  const name = addOn.name;
  const activeTo = addOn.held.activeTo!;
  const detailPath = addOnDetailsPath(addOnId);

  const handleNext = () => stepperRef.current?.next();

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(detailPath);
    } else {
      stepperRef.current?.prev();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    clearFailure();
    try {
      await resumeRemote.mutateAsync(addOnId);
      showSuccess(`${name} resumed.`);
      navigate(detailPath);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to resume this add-on. Please try again.";
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      label: "Details",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Details</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              You're about to resume the following add-on. Review the details, then continue to confirm.
            </p>
            <dl className="row mb-4">
              <dt className="col-bo-3 text-body-secondary fw-normal">Add-on</dt>
              <dd className="col-bo-9 fw-semibold">{name}</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Status</dt>
              <dd className="col-bo-9">Ending</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Access until</dt>
              <dd className="col-bo-9">{formatDateShort(activeTo)}</dd>
            </dl>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleNext}>
                Next: Review
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <CAlert color="success">
              This add-on was cancelled, with access set to end on {formatDateShort(activeTo)}. Resuming reverses
              that.
            </CAlert>
            <p>When you confirm:</p>
            <ul className="mb-4">
              <li>
                Your <strong>{name}</strong> add-on continues with no end date.
              </li>
              <li>Your access to it continues uninterrupted.</li>
              <li>
                Billing continues on your normal cycle. <Link to={routePaths.billing}>Billing</Link> shows what you'll
                pay.
              </li>
            </ul>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack} disabled={submitting}>
                Back
              </CButton>
              <CButton color="success" onClick={handleSubmit} disabled={submitting}>
                {submitting && <CSpinner size="sm" className="me-2" />}
                Resume add-on
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>Resume add-on</h2>
      <p className="text-body-secondary">
        Continue your <strong>{name}</strong> add-on.
      </p>
      <hr />

      <CStepper ref={stepperRef} steps={steps} onStepChange={setCurrentStep} onReset={() => setCurrentStep(1)} />
    </>
  );
}

export default AddOnResumeWizard;
